import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { PropertyService } from '../../services/property.service';
import { ModifyLeaseComponent } from '../modify-lease/modify-lease.component';

export interface ViewLeaseDialogData {
  lease: any;
  property?: any;
}

@Component({
  selector: 'app-view-lease-dialog',
  templateUrl: './view-lease-dialog.component.html',
  styleUrls: ['./view-lease-dialog.component.scss']
})
export class ViewLeaseDialogComponent implements OnInit, OnDestroy {
  lease: any = null;
  property: any = null;
  documents: any[] = [];
  selectedDocument: any = null;
  previewUrl: SafeResourceUrl | null = null;
  private objectUrl: string | null = null;
  private fileBytes: ArrayBuffer | null = null;
  private mimeType = 'application/pdf';
  previewKind: 'pdf' | 'image' | 'other' | null = null;
  loading = true;
  documentsLoading = false;
  generating = false;
  uploading = false;
  sharing = false;
  error: string | null = null;
  message: string | null = null;
  /** Narrow / touch UI: no in-dialog PDF iframe (native Open/Share instead). */
  isCompact = false;
  canShare = false;
  private mediaQuery: MediaQueryList;

  constructor(
    private propertyService: PropertyService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private router: Router,
    @Optional() public dialogRef: MatDialogRef<ViewLeaseDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ViewLeaseDialogData
  ) {}

  ngOnInit() {
    this.canShare = typeof navigator !== 'undefined' && !!(navigator as any).share;
    this.mediaQuery = typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 768px)')
      : null;
    this.isCompact = !!(this.mediaQuery && this.mediaQuery.matches);
    if (this.mediaQuery) {
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onViewportChange);
      } else if ((this.mediaQuery as any).addListener) {
        (this.mediaQuery as any).addListener(this.onViewportChange);
      }
    }

    const seedLease = this.data && this.data.lease;
    this.property = (this.data && this.data.property)
      || (seedLease && seedLease.property)
      || null;

    if (!seedLease || !seedLease.id) {
      this.error = 'No lease specified.';
      this.loading = false;
      return;
    }

    this.propertyService.getLeaseById(seedLease.id)
      .then((lease: any) => {
        this.lease = lease || seedLease;
        if (!this.property || !this.property.id) {
          this.property = lease && lease.property
            ? lease.property
            : { id: this.lease.propertyId };
        }
        this.loading = false;
        return this.loadDocuments();
      })
      .catch(err => {
        console.error('Failed to load lease:', err);
        this.lease = seedLease;
        this.loading = false;
        this.loadDocuments();
      });
  }

  ngOnDestroy() {
    if (this.mediaQuery) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.onViewportChange);
      } else if ((this.mediaQuery as any).removeListener) {
        (this.mediaQuery as any).removeListener(this.onViewportChange);
      }
    }
    this.revokePreview();
  }

  private onViewportChange = (event: MediaQueryListEvent | MediaQueryList) => {
    const matches = typeof (event as MediaQueryListEvent).matches === 'boolean'
      ? (event as MediaQueryListEvent).matches
      : (event as MediaQueryList).matches;
    this.isCompact = !!matches;
  }

  get propertyId(): string | null {
    return (this.property && this.property.id)
      || (this.lease && this.lease.propertyId)
      || null;
  }

  get isM2M(): boolean {
    return !!(this.lease && this.lease.type === 'M2M')
      || !!(this.lease && !this.lease.startDate && !this.lease.endDate);
  }

  get hasDocuments(): boolean {
    return Array.isArray(this.documents) && this.documents.length > 0;
  }

  get propertyLabel(): string {
    if (!this.property) return '';
    const parts = [
      this.property.addressLine1,
      this.property.city,
      this.property.state
    ].filter(Boolean);
    return parts.join(', ');
  }

  get selectedFileName(): string {
    return (this.selectedDocument && this.selectedDocument.originalFilename) || 'lease.pdf';
  }

  loadDocuments() {
    if (!this.propertyId || !this.lease || !this.lease.id) {
      return Promise.resolve();
    }
    this.documentsLoading = true;
    return this.propertyService.getLeaseDocuments(this.propertyId, this.lease.id)
      .then((docs: any[]) => {
        this.documents = Array.isArray(docs) ? docs : [];
        this.documentsLoading = false;
        if (this.documents.length > 0) {
          this.selectDocument(this.documents[0]);
        } else {
          this.clearPreview();
        }
      })
      .catch(err => {
        console.error('Failed to load lease documents:', err);
        this.documents = [];
        this.documentsLoading = false;
      });
  }

  selectDocument(doc: any) {
    if (!doc || !doc.id || !this.propertyId) return;
    this.selectedDocument = doc;
    this.message = null;
    this.documentsLoading = true;
    this.revokePreview();
    this.propertyService.getLeaseDocumentBlob(this.propertyId, doc.id)
      .then((blob: Blob) => this.readBlobAsArrayBuffer(blob).then((buffer) => {
        const type = (doc.contentType || blob.type || '').toLowerCase();
        if (type.indexOf('pdf') >= 0) {
          this.previewKind = 'pdf';
        } else if (type.indexOf('image/') === 0) {
          this.previewKind = 'image';
        } else {
          const name = (doc.originalFilename || '').toLowerCase();
          this.previewKind = name.endsWith('.pdf')
            ? 'pdf'
            : (/\.(png|jpe?g|gif|webp)$/.test(name) ? 'image' : 'other');
        }
        this.mimeType = blob.type
          || (this.previewKind === 'pdf' ? 'application/pdf' : (blob.type || 'application/octet-stream'));
        this.fileBytes = buffer.slice(0);
        const outBlob = new Blob([this.fileBytes], { type: this.mimeType || 'application/pdf' });
        this.objectUrl = window.URL.createObjectURL(outBlob);
        // Desktop can embed; mobile uses native Open instead of a cramped iframe.
        if (!this.isCompact || this.previewKind === 'image') {
          this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
        } else {
          this.previewUrl = null;
        }
        this.documentsLoading = false;
      }))
      .catch(err => {
        console.error('Failed to preview document:', err);
        this.previewKind = null;
        this.previewUrl = null;
        this.fileBytes = null;
        this.documentsLoading = false;
        this.message = 'Could not load the document preview. Try downloading instead.';
      });
  }

  private readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error || new Error('Failed to read document'));
      reader.readAsArrayBuffer(blob);
    });
  }

  /** Full-page viewer with a labeled Back control (preferred on mobile). */
  openFullscreenPage() {
    if (!this.propertyId || !this.selectedDocument || !this.selectedDocument.id) {
      return;
    }
    const queryParams: any = {
      fileName: this.selectedFileName
    };
    if (this.lease && this.lease.id) {
      queryParams.leaseId = this.lease.id;
    }
    if (this.dialogRef) {
      this.dialogRef.close({ navigated: true });
    }
    this.router.navigate(
      ['/lease-document', this.propertyId, this.selectedDocument.id],
      { queryParams }
    );
  }

  downloadSelected() {
    if (!this.selectedDocument || !this.propertyId) return;
    if (this.fileBytes) {
      const blob = new Blob([this.fileBytes], { type: this.mimeType || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.selectedFileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    }
    this.propertyService.downloadLeaseDocument(
      this.propertyId,
      this.selectedDocument.id,
      this.selectedFileName
    ).catch(err => {
      console.error('Download failed:', err);
      alert('Failed to download lease document.');
    });
  }

  shareSelected() {
    if (!this.fileBytes || this.sharing) {
      return;
    }
    const n: any = navigator;
    if (!n || typeof n.share !== 'function') {
      this.downloadSelected();
      return;
    }
    const fileName = this.selectedFileName;
    const message = 'Lease document: ' + fileName;
    const file = new File([this.fileBytes.slice(0)], fileName, {
      type: this.mimeType || 'application/pdf',
      lastModified: Date.now()
    });
    this.sharing = true;
    const finish = () => { this.sharing = false; };
    const canShareFiles = !n.canShare || n.canShare({ files: [file] });
    const sharePromise = canShareFiles
      ? n.share({ files: [file], text: message, title: message })
      : n.share({ text: message, title: message });
    sharePromise.then(finish).catch((err) => {
      finish();
      if (err && err.name === 'AbortError') {
        return;
      }
      console.error('Share failed:', err);
      this.downloadSelected();
    });
  }

  generateDocument() {
    if (!this.propertyId || !this.lease || !this.lease.id || this.generating) return;
    this.generating = true;
    this.message = null;
    this.propertyService.generateLeaseDocument(this.propertyId, this.lease.id)
      .then((result: any) => {
        this.generating = false;
        this.message = 'Lease summary PDF generated and attached.';
        if (result && Array.isArray(result.documents)) {
          this.documents = result.documents;
          if (result.document) {
            this.selectDocument(result.document);
          } else if (this.documents.length) {
            this.selectDocument(this.documents[0]);
          }
        } else {
          this.loadDocuments();
        }
      })
      .catch(err => {
        console.error('Generate lease document failed:', err);
        this.generating = false;
        const msg = (err && err.error && err.error.error)
          || 'Failed to generate lease document.';
        alert(msg);
      });
  }

  onUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files || !input.files.length || !this.propertyId || !this.lease) {
      return;
    }
    const file = input.files[0];
    this.uploading = true;
    this.message = null;
    this.propertyService.uploadLeaseDocument(this.propertyId, this.lease.id, file)
      .then((result: any) => {
        this.uploading = false;
        input.value = '';
        this.message = 'Document uploaded.';
        if (result && result.document) {
          return this.loadDocuments().then(() => this.selectDocument(result.document));
        }
        return this.loadDocuments();
      })
      .catch(err => {
        console.error('Upload failed:', err);
        this.uploading = false;
        input.value = '';
        alert((err && err.error && err.error.error) || 'Failed to upload document.');
      });
  }

  openEdit() {
    if (!this.lease || !this.propertyId) return;
    const property = this.property || { id: this.propertyId };
    const editRef = this.dialog.open(ModifyLeaseComponent, {
      width: '920px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: this.isCompact ? '100%' : undefined,
      autoFocus: false,
      panelClass: 'lease-dialog-panel',
      data: { lease: this.lease, property }
    });
    editRef.afterClosed().subscribe(() => {
      this.propertyService.getLeaseById(this.lease.id).then((lease: any) => {
        if (lease) this.lease = lease;
      }).catch(() => {});
      this.loadDocuments();
    });
  }

  close() {
    if (this.dialogRef) {
      this.dialogRef.close({ refreshed: true });
    }
  }

  private clearPreview() {
    this.selectedDocument = null;
    this.revokePreview();
    this.previewKind = null;
  }

  private revokePreview() {
    if (this.objectUrl) {
      window.URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    this.previewUrl = null;
    this.fileBytes = null;
  }
}
